/**
 * Dropdown list body for CreatableCombobox.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Plus } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { LabeledComboboxOption } from './creatable-combobox.helpers';

export interface CreatableComboboxListProps {
  labeledMode: boolean;
  filteredPlain: string[];
  filteredLabeled: LabeledComboboxOption[];
  presetCount: number;
  value: string;
  canCreate: boolean;
  createLabel: (v: string) => string;
  inputTrimmed: string;
  onSelect: (v: string) => void;
  onCreate: () => void;
}

export const CreatableComboboxList: FC<CreatableComboboxListProps> = ({
  labeledMode,
  filteredPlain,
  filteredLabeled,
  presetCount,
  value,
  canCreate,
  createLabel,
  inputTrimmed,
  onSelect,
  onCreate,
}) => {
  const emptyPresets = presetCount === 0;

  return (
    <div role="listbox" className="max-h-[240px] overflow-y-auto">
      {labeledMode ? (
        filteredLabeled.length === 0 && !canCreate ? (
          <div className="px-3 py-6 text-center">
            <p className="text-sm text-muted-foreground">
              {emptyPresets
                ? 'No preset values loaded. Type to filter…'
                : 'No matches. Type a custom filter…'}
            </p>
          </div>
        ) : (
          <>
            {filteredLabeled.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={opt.value === value}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                  'hover:bg-accent hover:text-accent-foreground',
                  opt.value === value && 'bg-accent text-accent-foreground',
                )}
                onClick={() => onSelect(opt.value)}
              >
                {opt.label}
              </button>
            ))}
            {canCreate && (
              <button
                type="button"
                role="option"
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                  'border-t border-border/50 mt-1 pt-2',
                  'text-primary hover:bg-primary/10 hover:text-primary',
                )}
                onClick={onCreate}
              >
                <Plus className="h-4 w-4 shrink-0" />
                {createLabel(inputTrimmed)}
              </button>
            )}
          </>
        )
      ) : filteredPlain.length === 0 && !canCreate ? (
        <div className="px-3 py-6 text-center">
          <p className="text-sm text-muted-foreground">
            {emptyPresets
              ? 'No groups yet. Type to create one.'
              : 'No matches. Type to create new.'}
          </p>
        </div>
      ) : (
        <>
          {filteredPlain.map((opt) => (
            <button
              key={opt}
              type="button"
              role="option"
              aria-selected={opt === value}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                opt === value && 'bg-accent text-accent-foreground',
              )}
              onClick={() => onSelect(opt)}
            >
              {opt}
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              role="option"
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                'border-t border-border/50 mt-1 pt-2',
                'text-primary hover:bg-primary/10 hover:text-primary',
              )}
              onClick={onCreate}
            >
              <Plus className="h-4 w-4 shrink-0" />
              {createLabel(inputTrimmed)}
            </button>
          )}
        </>
      )}
    </div>
  );
};
